import React, { useEffect } from "react";
import { EntityId } from "@reduxjs/toolkit";
import { fetchRewards, createReward, destroyReward, updateReward } from "src/actions/mine_craft/rewardsActions";
import { selectRewardEntities, selectRewardIds, setNewError, setEditError, setNewParams } from "src/store/mine_craft/rewardsSlice";
import { useAppDispatch, useAppSelector } from "src/store/hooks";
import { objToFormData } from "src/helpers/FormHelper";
import usePermissions from "src/hooks/usePermissions";
import Form from "./_Form";

import AlertBlock from "src/componets/AlertBlock";
import { ButtonDestroy, Button } from "src/componets/Buttons";
import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import { message, Button as ButtonAnt } from "antd";
import '../style.scss';

const _List = ({ challenge_id }: { challenge_id: number }) => {
  const dispatch = useAppDispatch();
  const ids = useAppSelector(selectRewardIds);
  const items = useAppSelector(selectRewardEntities);
  const newParams  = useAppSelector(state => state.mineCraft.rewards.newParams);
  const newError = useAppSelector(state => state.mineCraft.rewards.newError);
  const editError = useAppSelector(state => state.mineCraft.rewards.editError);
  const error = newError || editError;
  const { write_minecraft } = usePermissions();

  useEffect(() => {
    dispatch(fetchRewards({ challenge_id: challenge_id }));
  }, [challenge_id, dispatch]);

  function onCloseError() {
    dispatch(setNewError(null));
    dispatch(setEditError(null));
  }

  async function onCreate(data: any) {
    const formData = objToFormData(data);
    dispatch(createReward(formData));
  }

  function onUpdate(id: any, data: any) {
    const formData: any = objToFormData(data);
    dispatch(updateReward({ id, data: formData }));
  }

  function onClickAddItem() {
    const lastId = ids[ids.length - 1];
    const lastItem = items[lastId];
    const from = (lastItem?.to || lastItem?.from || 0) + 1;

    dispatch(setNewParams({ challenge_id: challenge_id, from }));
  }

  async function onClickDestroy(id: any) {
    const resultAction = await dispatch(destroyReward(id));

    if (destroyReward.fulfilled.match(resultAction)) {
      message.success(`ItemID ${id} was successful deleted`);
    }

    if (destroyReward.rejected.match(resultAction)) {
      message.error(resultAction.error.message);
    }
  }

  function onCloseNew() {
    dispatch(setNewParams(null));
  }

  const renderReward = (id: EntityId) => {
    const reward = items[id];

    if (!reward) { return; }

    if (!write_minecraft) {
      return (
        <li key={id}>
          <code>{reward.title}</code> — {reward.coins} coins — places {reward.from}{reward.to ? `-${reward.to}` : ''}
        </li>
      );
    }

    return (
      <div key={id} className='v-reward'>
        <ButtonDestroy title={null} onClick={() => onClickDestroy(id)} />
        <Form onFinish={(data: any) => onUpdate(id, data)} params={reward} />
      </div>
    );
  }

  const renderNewReward = () => {
    if (!newParams) {
      return null;
    }

    return (
      <div className='v-reward'>
        <Button onClick={onCloseNew} icon={CloseOutlined} />
        <Form onFinish={onCreate} params={newParams} />
      </div>
    )
  }

  if (!write_minecraft) {
    return (
      <div className='v-rewards-list'>
        <ul style={{ paddingLeft: 20 }}>
          {ids.map(renderReward)}
        </ul>
      </div>
    )
  }

  return (
    <div className='v-rewards-list'>
      <AlertBlock type='error' message={error} onClose={onCloseError} />

      <div className='v-rewards-body'>
        {ids.map(renderReward)}
        {renderNewReward()}
      </div>

      {
        !newParams &&
          <ButtonAnt
            type="dashed"
            onClick={onClickAddItem}
            block
            icon={<PlusOutlined />}
          >
            Add reward
          </ButtonAnt>
      }
    </div>
  )
}

export default _List;
